import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Theme,
  Box,
  TableSortLabel,
} from "@mui/material";
import { SortType } from "../../../models/SortType";
import { StyledTableRow } from "../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { useNavigate } from "react-router-dom";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import dayjs from "dayjs";
import CustomTypography from "../common/form/CustomTypography";
import { PurhcaseRequestDetails } from "../../../models/PurchaseRequest/PurchaseRequestDetails";
import { PurchaseRequestRejectedBy } from "../../../models/PurchaseRequest/PurchaseRequestBody";
import { observer } from "mobx-react";

interface PrListingTableProps {
  data: PurhcaseRequestDetails[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  onApprove: (prId: number, team: PurchaseRequestRejectedBy) => void;
  onReject: (prId: number, team: PurchaseRequestRejectedBy) => void;
  onGeneratePO: (prId: number) => void;
  onCancelPR: (prId: number) => void;
}

// const getApprovalDate = (
//   pr: PurhcaseRequestDetails,
//   type: "sourcing" | "finance" | "swageazy",
// ) => {
//   let rejectionDate, approvalDate;

//   switch (type) {
//     case "sourcing":
//       rejectionDate = pr.rejection_date_by_sourcing;
//       approvalDate = pr.sourcing_approval_date;
//       break;
//     case "finance":
//       rejectionDate = pr.rejection_date_by_finance;
//       approvalDate = pr.finance_approval_date;
//       break;
//     case "swageazy":
//       rejectionDate = pr.rejection_date_by_swageazy;
//       approvalDate = pr.swageazy_approval_date;
//       break;
//   }

//   if (rejectionDate) {
//     return dayjs(rejectionDate).format("DD/MM/YYYY HH:mm");
//   }
//   if (approvalDate) {
//     return dayjs(approvalDate).format("DD/MM/YYYY HH:mm");
//   }
//   return "-";
// };

const getApprovalStatus = (
  pr: PurhcaseRequestDetails,
  type: "sourcing" | "finance" | "swageazy",
) => {
  if (type === "sourcing") {
    if (Boolean(pr.sourcing_approval)) return "Approved";
    if (
      pr.rejected_by_sourcing_admin_id !== null &&
      pr.rejected_by_sourcing_admin_id > 0
    ) {
      return "Rejected";
    }
    return "Pending";
  }

  if (type === "finance") {
    if (Boolean(pr.finance_approval)) return "Approved";
    if (
      pr.rejected_by_finance_admin_id !== null &&
      pr.rejected_by_finance_admin_id > 0
    ) {
      return "Rejected";
    }
    return "Pending";
  }

  if (!pr.third_approval_required) return "--";
  if (Boolean(pr.swageazy_approval)) return "Approved";
  if (
    pr.rejected_by_swageazy_admin_id !== null &&
    pr.rejected_by_swageazy_admin_id > 0
  ) {
    return "Rejected";
  }
  return "Pending";
};

const getApprovalStatusColor = (status: string) => {
  if (status === "Approved") return "green";
  if (status === "Rejected") return "red";
  return "inherit";
};

const PrListingTable = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  minHeight = window.innerHeight - 350,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onApprove,
  onReject,
  onGeneratePO,
  onCancelPR,
}: PrListingTableProps) => {
  const theme = useTheme<Theme>();

  const navigate = useNavigate();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };
  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <AppTableHeaderCell
                sx={{
                  position: "sticky",
                  left: 0,
                  zIndex: 12,
                  backgroundColor: theme.palette.customColors.white,
                }}
              >
                <TableSortLabel
                  active={sortBy === "id"}
                  direction={sortType === SortType.ASC ? "asc" : "desc"}
                  onClick={() =>
                    onSortChange?.(
                      "id",
                      sortBy === "id" && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  IconComponent={() => <span>{renderSortIcon("id")}</span>}
                  sx={{ fontSize: "12px" }}
                >
                  PR ID
                </TableSortLabel>
              </AppTableHeaderCell>
              <AppTableHeaderCell
                sx={{
                  position: "sticky",
                  left: 60,
                  zIndex: 12,
                  backgroundColor: theme.palette.customColors.white,
                }}
                title="ORDER ID"
              />
              <AppTableHeaderCell title="PO ID" minWidth="200px" />
              <AppTableHeaderCell title="VENDOR" />
              <AppTableHeaderCell title="PR CREATED BY" />
              {/* <AppTableHeaderCell title="BILL FROM GST" />
              <AppTableHeaderCell title="BILL FROM STATE" /> */}
              <AppTableHeaderCell title="EXPECTED DELIVERY DATE" />
              <AppTableHeaderCell title="SOURCING STATUS" />
              <AppTableHeaderCell title="FINANCE STATUS" />
              <AppTableHeaderCell title="SWAGEAZY STATUS" />
              <AppTableHeaderCell title="PR STATUS" />
              {/* <AppTableHeaderCell title="EXPECTED PAYMENT DATE" /> */}
              {/* <AppTableHeaderCell title="SOURCING APPROVAL/REJECTION" /> */}
              {/* <AppTableHeaderCell title="SOURCING APPROVAL/REJECTION BY" /> */}
              {/* <AppTableHeaderCell title="SOURCING APPROVAL/REJECTION DATE" /> */}
              {/* <AppTableHeaderCell title="FINANCE APPROVAL/REJECTION" />
              <AppTableHeaderCell title="FINANCE APPROVAL/REJECTION BY" /> */}
              {/* <AppTableHeaderCell title="FINANCE APPROVAL/REJECTION DATE" /> */}
              {/* <AppTableHeaderCell title="SWAGEAZY APPROVAL/REJECTION" />
              <AppTableHeaderCell title="SWAGEAZY APPROVAL/REJECTION DATE" />    */}
              {/* <AppTableHeaderCell title="CANCEL PR" /> */}
              {/* <AppTableHeaderCell title="CANCELLATION REASON" />
              <AppTableHeaderCell title="CANCELLATION DATE" />
              <AppTableHeaderCell title="CANCELLED BY" /> */}
              {/* <AppTableHeaderCell title="Generated PO" /> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((item: PurhcaseRequestDetails) => (
              <StyledTableRow key={item.id}>
                <AppTableRowCell
                  minWidth="80px"
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                  onClick={() =>
                    navigate(
                      `/purchase/requests/details?prId=${item.id}&projectId=${item.order_id}`,
                    )
                  }
                >
                  {item.id}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    position: "sticky",
                    left: 60,
                    zIndex: 10,
                    backgroundColor: theme.palette.customColors.white,
                  }}
                >
                  {Number(item.order_id) === -1
                    ? "Swageazy Inventory"
                    : item.order_id || "-"}
                </AppTableRowCell>
                <AppTableRowCell
                  sx={{
                    flexWrap: "wrap",
                    wordWrap: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  }}
                >
                  {item.po_ids || "-"}
                </AppTableRowCell>
                <AppTableRowCell>{item.vendor_name || "-"}</AppTableRowCell>

                <AppTableRowCell>
                  {item.created_by_adminname || "-"}
                </AppTableRowCell>
                {/* <AppTableRowCell>{item.bill_from_gst || "-"}</AppTableRowCell>
                <AppTableRowCell>{item.bill_from_state || "-"}</AppTableRowCell> */}
                <AppTableRowCell>
                  {item.expected_delivery_date
                    ? dayjs(item.expected_delivery_date).format("DD/MM/YYYY")
                    : "-"}
                </AppTableRowCell>
                <AppTableRowCell>
                  <CustomTypography
                    variant="body2"
                    color={getApprovalStatusColor(
                      getApprovalStatus(item, "sourcing"),
                    )}
                  >
                    {getApprovalStatus(item, "sourcing")}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell>
                  <CustomTypography
                    variant="body2"
                    color={getApprovalStatusColor(
                      getApprovalStatus(item, "finance"),
                    )}
                  >
                    {getApprovalStatus(item, "finance")}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell>
                  <CustomTypography
                    variant="body2"
                    color={getApprovalStatusColor(
                      getApprovalStatus(item, "swageazy"),
                    )}
                  >
                    {getApprovalStatus(item, "swageazy")}
                  </CustomTypography>
                </AppTableRowCell>
                <AppTableRowCell>
                  {item.pr_cancellation_date ? (
                    <CustomTypography
                      color="red"
                      variant="body2"
                      fontWeight={600}
                    >
                      Cancelled
                    </CustomTypography>
                  ) : (
                    "Active"
                  )}
                </AppTableRowCell>
                {/* <AppTableRowCell>
                  {item.expected_payment_date
                    ? dayjs(item.expected_payment_date).format("DD/MM/YYYY")
                    : "-"}
                </AppTableRowCell> */}
                {/* <AppTableRowCell
                  sx={{
                    gap: 1,
                    textAlign: "center",
                  }}
                >
                  {(() => {
                    // Show buttons if not approved and not rejected

                    if (
                      Boolean(item.sourcing_approval) === false &&
                      (item.rejected_by_sourcing_admin_id === null ||
                        item.rejected_by_sourcing_admin_id <= 0)
                    ) {
                      if (
                        userStore.hasPermission(
                          EmployeeRoles.SOURCING_TEAM_PR_ACTIONS,
                        )
                      ) {
                        return (
                          <>
                            <AppButton
                              label={"Approve"}
                              variant={"primary"}
                              onClick={() => {
                                onApprove(
                                  item.id,
                                  PurchaseRequestRejectedBy.SOURCING,
                                );
                              }}
                              disabled={
                                item.active === 0 ||
                                (item.rejected_by_finance_admin_id &&
                                item.rejected_by_finance_admin_id > 0
                                  ? true
                                  : false)
                              }
                            />
                            <AppButton
                              label={"Reject"}
                              variant={"secondary"}
                              onClick={() => {
                                onReject(
                                  item.id,
                                  PurchaseRequestRejectedBy.SOURCING,
                                );
                              }}
                              disabled={
                                !!(
                                  item.finance_approval &&
                                  item.sourcing_approval
                                )
                              }
                              customStyles={{ marginLeft: "10px" }}
                            />
                          </>
                        );
                      } else {
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CustomTypography
                              color={theme.palette.customColors.info}
                              variant="body2"
                              fontWeight={600}
                            >
                              Pending Approval
                            </CustomTypography>
                          </Box>
                        );
                      }
                    } else if (Boolean(item.sourcing_approval)) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CustomTypography
                            color="green"
                            variant="body2"
                            fontWeight={600}
                          >
                            Approved
                          </CustomTypography>
                        </Box>
                      );
                    } else if (
                      item.rejected_by_sourcing_admin_id &&
                      item.rejected_by_sourcing_admin_id > 0
                    ) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CustomTypography
                            color="red"
                            variant="body2"
                            fontWeight={600}
                          >
                            Rejected
                          </CustomTypography>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {item.sourcing_approval
                    ? item.approved_by_sourcing_adminname
                    : item.rejected_by_sourcing_adminname || "-"}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {getApprovalDate(item, "sourcing")}
                </AppTableRowCell> */}

                {/* <AppTableRowCell
                  sx={{
                    gap: 1,
                    textAlign: "center",
                    minHeight: "48px",
                  }}
                >
                  {(() => {
                    // Show buttons if not approved and not rejected
                    if (
                      !item.finance_approval &&
                      (item.rejected_by_finance_admin_id === null ||
                        item.rejected_by_finance_admin_id <= 0)
                    ) {
                      if (
                        userStore.hasPermission(
                          EmployeeRoles.FINANCE_TEAM_PR_ACTIONS,
                        )
                      ) {
                        return (
                          <>
                            <AppButton
                              label={"Approve"}
                              variant={"primary"}
                              onClick={() => {
                                onApprove(
                                  item.id,
                                  PurchaseRequestRejectedBy.FINANCE,
                                );
                              }}
                              disabled={
                                item.active === 0 ||
                                (item.rejected_by_sourcing_admin_id &&
                                item.rejected_by_sourcing_admin_id > 0
                                  ? true
                                  : false)
                              }
                            />
                            <AppButton
                              label={"Reject"}
                              variant={"secondary"}
                              onClick={() => {
                                onReject(
                                  item.id,
                                  PurchaseRequestRejectedBy.FINANCE,
                                );
                              }}
                              disabled={
                                !!(
                                  item.finance_approval &&
                                  item.sourcing_approval
                                )
                              }
                              customStyles={{ marginLeft: "10px" }}
                            />
                          </>
                        );
                      }
                    } else if (item.finance_approval) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CustomTypography
                            color="green"
                            variant="body2"
                            fontWeight={600}
                          >
                            Approved
                          </CustomTypography>
                        </Box>
                      );
                    } else if (
                      item.rejected_by_finance_admin_id &&
                      item.rejected_by_finance_admin_id > 0
                    ) {
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CustomTypography
                            color="red"
                            variant="body2"
                            fontWeight={600}
                          >
                            Rejected
                          </CustomTypography>
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {item.finance_approval
                    ? item.approved_by_finance_adminname
                    : item.rejected_by_finance_adminname || "-"}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {getApprovalDate(item, "finance")}
                </AppTableRowCell> */}
                {/* <AppTableRowCell
                    sx={{
                      gap: 1,
                      textAlign: "center",
                      minHeight: "48px",
                    }}
                  >
                    {(() => {
                      if (item.third_approval_required) {
                        if (
                          !item.swageazy_approval &&
                          (item.rejected_by_swageazy_admin_id === null ||
                            item.rejected_by_swageazy_admin_id <= 0)
                        ) {
                          if (
                            userStore.hasPermission(
                              EmployeeRoles.SWAGEAZY_INVENTORY_PR_ACTIONS,
                            )
                          ) {
                            return (
                              <>
                                <AppButton
                                  label={"Approve"}
                                  variant={"primary"}
                                  onClick={() => {
                                    onApprove(
                                      item.id,
                                      PurchaseRequestRejectedBy.SWAGEAZY,
                                    );
                                  }}
                                  disabled={item.active === 0}
                                />
                                <AppButton
                                  label={"Reject"}
                                  variant={"secondary"}
                                  onClick={() => {
                                    onReject(
                                      item.id,
                                      PurchaseRequestRejectedBy.SWAGEAZY,
                                    );
                                  }}
                                  disabled={item.active === 0}
                                  customStyles={{ marginLeft: "10px" }}
                                />
                              </>
                            );
                          }
                        } else if (item.swageazy_approval) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CustomTypography
                                color="green"
                                variant="body2"
                                fontWeight={600}
                              >
                                Approved
                              </CustomTypography>
                            </Box>
                          );
                        } else if (
                          item.rejected_by_swageazy_admin_id &&
                          item.rejected_by_swageazy_admin_id > 0
                        ) {
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CustomTypography
                                color="red"
                                variant="body2"
                                fontWeight={600}
                              >
                                Rejected
                              </CustomTypography>
                            </Box>
                          );
                        }
                      } else {
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CustomTypography
                              color="grey"
                              variant="body2"
                              fontWeight={600}
                            >
                              --
                            </CustomTypography>
                          </Box>
                        );
                      }
                      return null;
                    })()}
                  </AppTableRowCell> 
                  <AppTableRowCell>
                    {item.third_approval_required
                      ? getApprovalDate(item, "swageazy")
                      : "-"}
                  </AppTableRowCell>  */}
                {/* <AppTableRowCell>
                  {item.pr_cancellation_date ? (
                    <CustomTypography
                      color="red"
                      variant="body2"
                      fontWeight={600}
                    >
                      Cancelled
                    </CustomTypography>
                  ) : (
                    "-"
                  )}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {item.pr_cancellation_reason || "-"}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {item.pr_cancellation_date
                    ? dayjs(item.pr_cancellation_date).format(
                        "DD/MM/YYYY HH:mm",
                      )
                    : "-"}
                </AppTableRowCell> */}
                {/* <AppTableRowCell>
                  {item.pr_cancelled_by_admin_name || "-"}
                </AppTableRowCell> */}

                {/* <AppTableRowCell>
                  {item.po_id > 0 ? (
                    <CustomTypography
                      color="green"
                      variant="body2"
                      fontWeight={600}
                    >
                      PO Generated
                    </CustomTypography>
                  ) : (
                    "-"
                  )}
                </AppTableRowCell> */}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 15,
            }}
          >
            <CustomTypography
              color={theme.palette.customColors.greyTextColor}
              variant="body2"
              fontWeight={700}
            >
              NO DATA FOUND!
            </CustomTypography>
          </Box>
        )}
      </TableContainer>
      <AppTablePagenation
        fontSize="0.8rem"
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={handleRowsChange}
      />
    </>
  );
};

export default observer(PrListingTable);
